/**
 * Prompt Section Base Class
 * 
 * Abstract base class for standardizing prompt section handling across all agents.
 * Each section (intro, tools, suggestions, contextNote, evaluation) extends this class
 * to provide Keywords AI integration with code fallbacks.
 * 
 * ## Usage Pattern
 * 
 * ```typescript
 * // 1. Define a section class
 * class OrchestratorIntroSection extends PromptSectionBase {
 *     protected readonly keywordsKey = 'ORCHESTRATOR_INTRO';
 *     protected readonly fallbackContent = `You are an orchestrator agent...`;
 * }
 * 
 * // 2. Create singleton instance
 * const introSection = new OrchestratorIntroSection();
 * 
 * // 3. Export getter function
 * export function getIntroSection(): string {
 *     return introSection.getContent();
 * }
 * 
 * // 4. With variables
 * export function getIntroSectionWithVariables(vars: Record<string, string>): string {
 *     return introSection.getContentWithVariables(vars);
 * }
 * ```
 * 
 * ## Benefits
 * 
 * - Enforces consistent structure across all agents
 * - Centralizes Keywords AI integration logic
 * - Type-safe variable replacement
 * - Reduces boilerplate in agent prompt files
 * - Makes it clear what each section needs (keywordsKey + fallback)
 */

import { getPromptService } from '../../../prompts';

// ═══════════════════════════════════════════════════════════════════════════
// ABSTRACT BASE CLASS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Abstract base class for prompt sections.
 * 
 * Each agent's prompt section (intro, suggestions, etc.) should extend this class
 * and provide:
 * - keywordsKey: The Keywords AI prompt name (e.g., 'ORCHESTRATOR_INTRO')
 * - fallbackContent: Static string to use when Keywords AI is unavailable
 */
export abstract class PromptSectionBase {
    /**
     * Keywords AI key for this section.
     * Format: {AGENT}_{SECTION} (e.g., 'ORCHESTRATOR_INTRO', 'PLANNER_SUGGESTIONS')
     */
    protected abstract readonly keywordsKey: string;
    
    /**
     * Code fallback content.
     * Used when Keywords AI is unavailable or the prompt doesn't exist.
     * Can include {{variables}} for replacement.
     */
    protected abstract readonly fallbackContent: string;
    
    /**
     * Get section content from Keywords AI with code fallback.
     * Automatically uses PromptService cache (no network call if cached).
     * 
     * @returns The section content (from Keywords AI or fallback)
     */
    public getContent(): string {
        return getPromptService().getPrompt(
            this.keywordsKey,
            this.fallbackContent
        );
    }
    
    /**
     * Get section content with variable replacement.
     * Supports {{variableName}} syntax in both Keywords AI and fallback content.
     * 
     * @param variables - Map of variable names to values
     * @returns The section content with variables replaced
     * 
     * @example
     * const intro = new OrchestratorIntroSection();
     * const content = intro.getContentWithVariables({
     *     currentDate: '2026-01-25',
     *     deckName: 'Q4 Review',
     * });
     */
    public getContentWithVariables(variables: Record<string, string>): string {
        return getPromptService().getPromptWithVariables(
            this.keywordsKey,
            this.fallbackContent,
            variables
        );
    }
    
    /**
     * Get the Keywords AI key for this section.
     * Useful for admin panel display.
     */
    public getKeywordsKey(): string {
        return this.keywordsKey;
    }
    
    /**
     * Get the fallback content.
     * Useful for testing or when you want to bypass Keywords AI.
     */
    public getFallbackContent(): string {
        return this.fallbackContent;
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Type helper for creating section instances
 */
export type PromptSection = PromptSectionBase;

/**
 * Interface for section class constructors (for factory patterns)
 */
export interface PromptSectionConstructor {
    new (): PromptSectionBase;
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default PromptSectionBase;
